import React from 'react';
import PropTypes from 'prop-types';
import { FONTS } from '../constants/Fonts';
import HousieLeaderboardRow from './HousieLeaderboardRow';
import LeaderboardPodium from './LeaderboardPodium';
import LeaderboardCongratsBanner from './LeaderboardCongratsBanner';
import LeaderboardPromoSidebar from './LeaderboardPromoSidebar';
import LeaderboardAdsSlider from './LeaderboardAdsSlider';
import TournamentWinnerPrizesSidebar from './TournamentWinnerPrizesSidebar';
import GameScreenTopBar from './GameScreenTopBar';
import { HOUSIE_GLASS_CARD_STYLE } from '../constants/housieLiveStyles';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { expandTournamentPrizeAllotments } from '../utils/utils';
import './LeaderboardScreen.css';

/** Figma 18110:2758 — column header label (light theme) */
const HEADER_CELL_STYLE = {
  fontFamily: FONTS.INTER_SEMIBOLD,
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: 0.8,
  color: '#003',
  textAlign: 'left',
};

/**
 * Figma 18110:2758 — leaderboard layout:
 * header · congrats + podium · list panel | RapBazaar promo sidebar
 */
const LeaderboardScreen = ({
  headerTitle,
  brandIconSrc,
  brandName,
  brandGradientFrom,
  brandGradientTo,
  onBack,
  columns,
  players,
  showBoard,
  hasWinners = true,
  emptyLabel,
  congratsLabel = TEXT_LABELS.RAP_HOUSIE_LEADERBOARD_SUBTITLE,
  headerExtra = null,
  promoBannerSrc,
  promoFeatures,
  promoBanners,
  allRoundsCompleted = false,
  rewardStatus = false,
  prizeAllotments,
  prizeWinners,
  prizeHeading,
  prizeTitle,
  prizeSubtitle,
  hideBack = false,
  variant = 'default',
}) => {
  const isTournament = variant === 'tournament';
  const topThree = hasWinners ? players.slice(0, 3) : [];
  const listPlayers = hasWinners ? players.slice(3) : players;
  const showEmptyPodium = !hasWinners && players.length > 0;
  const adSlides = (Array.isArray(promoBanners) ? promoBanners : []).filter(
    (item) => item?.src || item?.image
  );
  const completedPrizes = expandTournamentPrizeAllotments(
    (Array.isArray(prizeAllotments) ? prizeAllotments : []).filter(
      (item) => item?.rank != null || item?.rankFrom != null
    ),
    Array.isArray(prizeWinners) ? prizeWinners : [],
    isTournament ? players : []
  );
  const showWinnerPrizes =
    isTournament && Boolean(rewardStatus) && completedPrizes.length > 0;

  return (
    <>
      <GameScreenTopBar
        title={headerTitle}
        brandIconSrc={brandIconSrc}
        brandName={brandName}
        brandGradientFrom={brandGradientFrom}
        brandGradientTo={brandGradientTo}
        backLabel="Back to Previous Page"
        onBack={onBack}
        variant="leaderboard"
        hideBack={isTournament && hideBack}
      />

      <div
        className={`lb-screen${headerExtra ? ' lb-screen--with-stepper' : ''}${
          isTournament ? ' lb-screen--tournament' : ''
        }`}
      >
        {headerExtra}

        <div className="lb-screen__main">
          <div
            className="lb-screen__podium-panel"
            style={{
              ...HOUSIE_GLASS_CARD_STYLE,
              opacity: showBoard ? 1 : 0.4,
              transition: 'opacity 0.6s ease',
            }}
          >
            <LeaderboardCongratsBanner
              label={congratsLabel}
              dynamicLabel={isTournament}
            />
            <LeaderboardPodium
              topThree={topThree}
              showEmpty={showEmptyPodium}
              tournamentScoring={isTournament}
            />
          </div>

          <div
            className="lb-screen__list-panel"
            style={{
              ...HOUSIE_GLASS_CARD_STYLE,
              opacity: showBoard ? 1 : 0.4,
              transition: 'opacity 0.6s ease',
              overflow: 'hidden',
            }}
            data-node-id="18110:2758-list"
          >
            <div className="lb-screen__list-header">
              <span className="lb-screen__col--rank" style={HEADER_CELL_STYLE}>
                {columns.rank}
              </span>
              <span className="lb-screen__col--player" style={HEADER_CELL_STYLE}>
                {columns.player}
              </span>
              <span className="lb-screen__col--points" style={HEADER_CELL_STYLE}>
                {columns.points}
              </span>
              <span className="lb-screen__col--won" style={HEADER_CELL_STYLE}>
                {columns.won}
              </span>
            </div>

            <div className="lb-screen__list-body">
              {listPlayers.length > 0 ? (
                listPlayers.map((player, index) => (
                  <HousieLeaderboardRow
                    key={player?.userId || player?.id || `lb-${index + 1}`}
                    rank={player?.rank || (hasWinners ? index + 4 : index + 1)}
                    userData={player}
                    tournamentScoring={isTournament}
                  />
                ))
              ) : players.length > 0 && hasWinners ? (
                <p className="lb-screen__empty-hint" style={{ fontFamily: FONTS.INTER_REGULAR }}>
                  Top players are shown above
                </p>
              ) : (
                <p className="lb-screen__empty-hint" style={{ fontFamily: FONTS.INTER_REGULAR }}>
                  {emptyLabel}
                </p>
              )}
            </div>
          </div>
        </div>

        {showWinnerPrizes ? (
          <TournamentWinnerPrizesSidebar
            prizes={completedPrizes}
            heading={prizeHeading}
            title={prizeTitle}
            subtitle={prizeSubtitle}
            autoPlay={false}
          />
        ) : isTournament && adSlides.length ? (
          <LeaderboardAdsSlider
            banners={adSlides}
            autoPlay={!rewardStatus}
          />
        ) : (
          <LeaderboardPromoSidebar
            bannerSrc={promoBannerSrc}
            features={promoFeatures}
          />
        )}
      </div>
    </>
  );
};

LeaderboardScreen.propTypes = {
  headerTitle: PropTypes.string.isRequired,
  brandIconSrc: PropTypes.string,
  brandName: PropTypes.string,
  brandGradientFrom: PropTypes.string,
  brandGradientTo: PropTypes.string,
  onBack: PropTypes.func,
  columns: PropTypes.shape({
    rank: PropTypes.string,
    player: PropTypes.string,
    points: PropTypes.string,
    won: PropTypes.string,
  }).isRequired,
  players: PropTypes.array,
  showBoard: PropTypes.bool,
  hasWinners: PropTypes.bool,
  emptyLabel: PropTypes.string,
  congratsLabel: PropTypes.string,
  headerExtra: PropTypes.node,
  promoBannerSrc: PropTypes.string,
  promoFeatures: PropTypes.arrayOf(PropTypes.string),
  promoBanners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      src: PropTypes.string,
      image: PropTypes.string,
      alt: PropTypes.string,
      url: PropTypes.string,
      order: PropTypes.number,
    })
  ),
  allRoundsCompleted: PropTypes.bool,
  rewardStatus: PropTypes.bool,
  hideBack: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'tournament']),
  prizeAllotments: PropTypes.arrayOf(
    PropTypes.shape({
      rank: PropTypes.number,
      rankFrom: PropTypes.number,
      rankTo: PropTypes.number,
      prizeTitle: PropTypes.string,
      prizeIcon: PropTypes.string,
      username: PropTypes.string,
      avatar: PropTypes.string,
    })
  ),
  prizeWinners: PropTypes.arrayOf(
    PropTypes.shape({
      rank: PropTypes.number,
      userName: PropTypes.string,
      username: PropTypes.string,
      profilePic: PropTypes.string,
      avatar: PropTypes.string,
    })
  ),
  prizeHeading: PropTypes.string,
  prizeTitle: PropTypes.string,
  prizeSubtitle: PropTypes.string,
};

LeaderboardScreen.defaultProps = {
  players: [],
  showBoard: true,
};

export default LeaderboardScreen;
